import React, { useState } from "react";
import {
  Card,
  Container,
  Form,
  Button,
  Alert,
  InputGroup,
  Modal,
} from "react-bootstrap";
import { FaEye, FaEyeSlash, FaUser, FaStore } from "react-icons/fa";

const DeleteAccount = () => {
  const [accountType, setAccountType] = useState("user"); // 'user' or 'vendor'
  const [identifier, setIdentifier] = useState(""); // email or mobile
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const handleDeleteRequest = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Validate input
    if (!identifier || !password) {
      setError("Please fill in all fields");
      return;
    }

    // Show confirmation modal
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    setError("");
    setSuccess("");
    setShowConfirmModal(false);

    try {
      const bases = Array.from(
        new Set([process.env.REACT_APP_API_BASE_URL].filter(Boolean))
      );

      let deleted = false;
      let lastErr = null;

      for (const base of bases) {
        try {
          const res = await fetch(`${base}/api/auth/delete-account`, {
            method: "DELETE",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              accountType,
              identifier,
              password,
            }),
          });
          console.log("Delete account response:", res);
          if (!res.ok) {
            const errorData = await res.json();
            lastErr = new Error(
              errorData.error || errorData.message || "Failed to delete account"
            );
            continue;
          }

          const data = await res.json();
          deleted = true;
          setSuccess(
            data.message ||
              `${
                accountType === "user" ? "User" : "Vendor"
              } account deleted successfully. Redirecting...`
          );

          // Redirect after 3 seconds
          setTimeout(() => {
            window.location.href = "/";
          }, 3000);

          break;
        } catch (e) {
          lastErr = e;
          continue;
        }
      }

      if (!deleted) {
        throw lastErr || new Error("Failed to delete account");
      }
    } catch (e) {
      console.error("Delete account error:", e);
      setError(
        e.message ||
          "Failed to delete account. Please check your credentials and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container
      className="d-flex align-items-center justify-content-center"
      style={{ minHeight: "70vh" }}
    >
      <Card style={{ maxWidth: 500, width: "100%" }}>
        <Card.Body>
          <Card.Title className="mb-3 text-danger">Delete Account</Card.Title>
          <Card.Text className="text-muted mb-4">
            Warning: This action will permanently delete your account. This
            cannot be undone.
          </Card.Text>

          {error && (
            <Alert variant="danger" onClose={() => setError("")} dismissible>
              {error}
            </Alert>
          )}
          {success && (
            <Alert variant="success" onClose={() => setSuccess("")} dismissible>
              {success}
            </Alert>
          )}

          <Form onSubmit={handleDeleteRequest}>
            <Form.Group className="mb-3" controlId="accountType">
              <Form.Label>Account Type</Form.Label>
              <Form.Select
                value={accountType}
                onChange={(e) => setAccountType(e.target.value)}
                disabled={loading}
                required
              >
                <option value="user">
                  <FaUser /> User Account
                </option>
                <option value="vendor">
                  <FaStore /> Vendor Account
                </option>
              </Form.Select>
              <Form.Text className="text-muted">
                Select the type of account you want to delete
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3" controlId="userIdentifier">
              <Form.Label>Email or Mobile Number</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter your email or mobile number"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                autoFocus
                required
                disabled={loading}
              />
              <Form.Text className="text-muted">
                Enter the email or mobile number associated with your{" "}
                {accountType} account
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-4" controlId="userPassword">
              <Form.Label>Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </Button>
              </InputGroup>
            </Form.Group>

            <div className="d-grid gap-2">
              <Button type="submit" variant="danger" disabled={loading}>
                {loading
                  ? "Processing..."
                  : `Delete My ${
                      accountType === "user" ? "User" : "Vendor"
                    } Account`}
              </Button>
            </div>
          </Form>

          <Alert variant="warning" className="mt-4 mb-0">
            <strong>
              What happens when you delete your {accountType} account:
            </strong>
            <ul className="mb-0 mt-2">
              <li>Your account will be permanently deleted</li>
              <li>You will lose access to all your data</li>
              {accountType === "vendor" && (
                <>
                  <li>All your products and services will be removed</li>
                  <li>Your vendor profile will be deleted</li>
                </>
              )}
              <li>This action cannot be reversed</li>
            </ul>
          </Alert>
        </Card.Body>
      </Card>

      {/* Confirmation Modal */}
      <Modal
        show={showConfirmModal}
        onHide={() => !loading && setShowConfirmModal(false)}
        backdrop={loading ? "static" : true}
        keyboard={!loading}
      >
        <Modal.Header closeButton={!loading}>
          <Modal.Title>Confirm Account Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger">
            <strong>Are you absolutely sure?</strong>
          </Alert>
          <p>
            This will permanently delete your{" "}
            {accountType === "user" ? "user" : "vendor"} account. All your data
            will be lost and this action cannot be undone.
          </p>
          <p className="mb-0">
            <strong>Account Type:</strong>{" "}
            {accountType === "user" ? "User" : "Vendor"}
            <br />
            <strong>Identifier:</strong> {identifier}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowConfirmModal(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmDelete}
            disabled={loading}
          >
            {loading ? "Deleting..." : "Yes, Delete My Account"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default DeleteAccount;
